import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { 
  TrendingUp, 
  Hash,
  Clock,
  Sparkles,
  Loader2,
  Copy,
  Check,
  History,
  Lightbulb,
  Instagram,
  Twitter,
  Facebook,
  Linkedin,
  Youtube,
  ArrowLeft,
  Zap,
  Target,
  Calendar,
  Users
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TrendsPage = ({ user, logout, token }) => {
  const [activeTab, setActiveTab] = useState("trends");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  // Trends state
  const [trendsForm, setTrendsForm] = useState({
    industry: "",
    platforms: ["instagram", "twitter", "tiktok"],
    language: "es"
  });
  const [trendsResult, setTrendsResult] = useState(null);
  
  // Hashtags state
  const [hashtagsForm, setHashtagsForm] = useState({
    topic: "",
    platform: "instagram",
    count: 20
  });
  const [hashtagsResult, setHashtagsResult] = useState(null);
  
  // Hotspots state
  const [hotspotsForm, setHotspotsForm] = useState({
    platform: "instagram",
    audience_type: "general"
  });
  const [hotspotsResult, setHotspotsResult] = useState(null);

  const platformIcons = {
    instagram: <Instagram className="w-4 h-4" />,
    twitter: <Twitter className="w-4 h-4" />,
    facebook: <Facebook className="w-4 h-4" />,
    linkedin: <Linkedin className="w-4 h-4" />,
    youtube: <Youtube className="w-4 h-4" />,
    tiktok: <span className="text-xs font-bold">TT</span>
  };

  const platformOptions = [
    { id: "instagram", name: "Instagram", color: "bg-pink-500" },
    { id: "twitter", name: "Twitter/X", color: "bg-blue-500" },
    { id: "tiktok", name: "TikTok", color: "bg-gray-800" },
    { id: "linkedin", name: "LinkedIn", color: "bg-sky-600" },
    { id: "facebook", name: "Facebook", color: "bg-indigo-600" },
    { id: "youtube", name: "YouTube", color: "bg-red-600" }
  ];

  const audienceTypes = [
    { id: "general", name: "General", description: "Audiencia diversa" },
    { id: "business", name: "Negocios", description: "B2B y empresarios" },
    { id: "youth", name: "Jóvenes", description: "Gen Z (16-25)" },
    { id: "professional", name: "Profesionales", description: "25-45 años" },
    { id: "parents", name: "Padres", description: "Familias" }
  ];

  useEffect(() => {
    fetchHistory();
  }, [token]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trends/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAnalyzeTrends = async () => {
    if (!trendsForm.industry.trim()) {
      toast.error("Ingresa tu industria o nicho");
      return;
    }

    setLoading(true);
    setTrendsResult(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/trends/analyze`,
        trendsForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrendsResult(response.data);
      toast.success("Análisis completado");
      fetchHistory();
    } catch (error) {
      toast.error("Error en el análisis");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateHashtags = async () => {
    if (!hashtagsForm.topic.trim()) {
      toast.error("Ingresa un tema para los hashtags");
      return;
    }

    setLoading(true);
    setHashtagsResult(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/trends/hashtags`,
        hashtagsForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHashtagsResult(response.data);
      toast.success("Hashtags generados");
      fetchHistory();
    } catch (error) {
      toast.error("Error generando hashtags");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeHotspots = async () => {
    setLoading(true);
    setHotspotsResult(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/trends/hotspots`,
        hotspotsForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHotspotsResult(response.data);
      toast.success("Análisis de horarios completado");
      fetchHistory();
    } catch (error) {
      toast.error("Error en el análisis");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copiado al portapapeles");
  };

  const togglePlatform = (platform) => {
    setTrendsForm(prev => {
      const platforms = prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform];
      return { ...prev, platforms };
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "trends": return <TrendingUp className="w-4 h-4" />;
      case "hashtags": return <Hash className="w-4 h-4" />;
      case "hotspots": return <Clock className="w-4 h-4" />;
      default: return <Lightbulb className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "trends": return "Tendencias";
      case "hashtags": return "Hashtags";
      case "hotspots": return "Horarios";
      default: return type;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link 
              to="/ideas" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a Ideas
            </Link>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                <TrendingUp className="w-6 h-6 text-purple-500" />
              </div>
              <h1 className="text-3xl font-bold" data-testid="trends-page-title">Trends</h1>
            </div>
            <p className="text-muted-foreground">
              Descubre tendencias, genera hashtags y encuentra los mejores horarios para publicar
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-4 w-full max-w-lg">
              <TabsTrigger value="trends" data-testid="tab-trends">
                <TrendingUp className="w-4 h-4 mr-2" />
                Tendencias
              </TabsTrigger>
              <TabsTrigger value="hashtags" data-testid="tab-hashtags">
                <Hash className="w-4 h-4 mr-2" />
                Hashtags
              </TabsTrigger>
              <TabsTrigger value="hotspots" data-testid="tab-hotspots">
                <Clock className="w-4 h-4 mr-2" />
                Horarios
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <History className="w-4 h-4 mr-2" />
                Historial
              </TabsTrigger>
            </TabsList>

            {/* TRENDS TAB */}
            <TabsContent value="trends" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-500" />
                    Análisis de Tendencias
                  </CardTitle>
                  <CardDescription>
                    Descubre qué contenido está funcionando en tu industria
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Tu industria o nicho</Label>
                    <Input
                      placeholder="Ej: Fitness, Marketing Digital, Gastronomía, Moda..."
                      value={trendsForm.industry}
                      onChange={(e) => setTrendsForm({ ...trendsForm, industry: e.target.value })}
                      data-testid="trends-industry-input"
                    />
                  </div>

                  <div>
                    <Label className="mb-3 block">Plataformas a analizar</Label>
                    <div className="flex flex-wrap gap-2">
                      {platformOptions.map((platform) => (
                        <div
                          key={platform.id}
                          onClick={() => togglePlatform(platform.id)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                            trendsForm.platforms.includes(platform.id)
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <Checkbox
                            checked={trendsForm.platforms.includes(platform.id)}
                            onCheckedChange={() => togglePlatform(platform.id)}
                          />
                          <span className={`p-1 rounded text-white ${platform.color}`}>
                            {platformIcons[platform.id]}
                          </span>
                          <span className="text-sm">{platform.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    className="w-full gradient-neural glow-neural text-white"
                    onClick={handleAnalyzeTrends}
                    disabled={loading}
                    data-testid="analyze-trends-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analizando tendencias...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Analizar Tendencias
                      </>
                    )}
                  </Button>

                  {trendsResult && (
                    <div className="p-4 bg-secondary/50 rounded-lg border border-border" data-testid="trends-result">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-purple-500" />
                          Tendencias para "{trendsResult.industry}"
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(trendsResult.analysis)}
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <pre className="whitespace-pre-wrap text-sm bg-background p-4 rounded-lg overflow-auto max-h-[500px]">
                        {trendsResult.analysis}
                      </pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* HASHTAGS TAB */}
            <TabsContent value="hashtags" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Hash className="w-5 h-5 text-cyan-500" />
                    Generador de Hashtags
                  </CardTitle>
                  <CardDescription>
                    Obtén los mejores hashtags para tu contenido
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Tema del contenido</Label>
                    <Input
                      placeholder="Ej: Recetas saludables, Tips de productividad, Viajes..."
                      value={hashtagsForm.topic}
                      onChange={(e) => setHashtagsForm({ ...hashtagsForm, topic: e.target.value })}
                      data-testid="hashtags-topic-input"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Plataforma</Label>
                      <Select
                        value={hashtagsForm.platform}
                        onValueChange={(v) => setHashtagsForm({ ...hashtagsForm, platform: v })}
                      >
                        <SelectTrigger data-testid="hashtags-platform-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {platformOptions.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              <div className="flex items-center gap-2">
                                {platformIcons[p.id]}
                                {p.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cantidad de hashtags</Label>
                      <Select
                        value={hashtagsForm.count.toString()}
                        onValueChange={(v) => setHashtagsForm({ ...hashtagsForm, count: parseInt(v) })}
                      >
                        <SelectTrigger data-testid="hashtags-count-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 hashtags</SelectItem>
                          <SelectItem value="15">15 hashtags</SelectItem>
                          <SelectItem value="20">20 hashtags</SelectItem>
                          <SelectItem value="30">30 hashtags</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                    onClick={handleGenerateHashtags}
                    disabled={loading}
                    data-testid="generate-hashtags-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generando hashtags...
                      </>
                    ) : (
                      <>
                        <Hash className="w-4 h-4 mr-2" />
                        Generar Hashtags
                      </>
                    )}
                  </Button>

                  {hashtagsResult && (
                    <div className="p-4 bg-secondary/50 rounded-lg border border-border" data-testid="hashtags-result">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Hash className="w-4 h-4 text-cyan-500" />
                          Hashtags para "{hashtagsResult.topic}"
                          <Badge variant="outline">{hashtagsResult.platform}</Badge>
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(hashtagsResult.hashtags)}
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <pre className="whitespace-pre-wrap text-sm bg-background p-4 rounded-lg overflow-auto max-h-[500px]">
                        {hashtagsResult.hashtags}
                      </pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* HOTSPOTS TAB */}
            <TabsContent value="hotspots" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-orange-500" />
                    Mejores Horarios (Hotspots)
                  </CardTitle>
                  <CardDescription>
                    Descubre cuándo publicar para máximo engagement
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Plataforma</Label>
                      <Select
                        value={hotspotsForm.platform}
                        onValueChange={(v) => setHotspotsForm({ ...hotspotsForm, platform: v })}
                      >
                        <SelectTrigger data-testid="hotspots-platform-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {platformOptions.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              <div className="flex items-center gap-2">
                                {platformIcons[p.id]}
                                {p.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipo de audiencia</Label>
                      <Select
                        value={hotspotsForm.audience_type}
                        onValueChange={(v) => setHotspotsForm({ ...hotspotsForm, audience_type: v })}
                      >
                        <SelectTrigger data-testid="hotspots-audience-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {audienceTypes.map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.name} - {a.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {audienceTypes.map((audience) => (
                      <div
                        key={audience.id}
                        onClick={() => setHotspotsForm({ ...hotspotsForm, audience_type: audience.id })}
                        className={`p-3 border rounded-lg cursor-pointer transition-all text-center ${
                          hotspotsForm.audience_type === audience.id
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-border hover:border-orange-500/50'
                        }`}
                      >
                        <Users className={`w-5 h-5 mx-auto mb-1 ${
                          hotspotsForm.audience_type === audience.id ? 'text-orange-500' : 'text-muted-foreground'
                        }`} />
                        <span className="text-xs font-medium">{audience.name}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={handleAnalyzeHotspots}
                    disabled={loading}
                    data-testid="analyze-hotspots-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analizando horarios...
                      </>
                    ) : (
                      <>
                        <Calendar className="w-4 h-4 mr-2" />
                        Analizar Mejores Horarios
                      </>
                    )}
                  </Button>

                  {hotspotsResult && (
                    <div className="p-4 bg-secondary/50 rounded-lg border border-border" data-testid="hotspots-result">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Clock className="w-4 h-4 text-orange-500" />
                          Horarios para {platformOptions.find(p => p.id === hotspotsResult.platform)?.name}
                          <Badge variant="outline">
                            {audienceTypes.find(a => a.id === hotspotsResult.audience_type)?.name}
                          </Badge>
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(hotspotsResult.hotspots)}
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <pre className="whitespace-pre-wrap text-sm bg-background p-4 rounded-lg overflow-auto max-h-[500px]">
                        {hotspotsResult.hotspots}
                      </pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* HISTORY TAB */}
            <TabsContent value="history" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-muted-foreground" />
                    Historial de Análisis
                  </CardTitle>
                  <CardDescription>
                    Tus análisis anteriores guardados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-8">
                      <History className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground mb-4">
                        No tienes análisis guardados aún
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Realiza un análisis de tendencias, hashtags u horarios para comenzar
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {history.map((item) => (
                        <div
                          key={item.trend_id || item.hashtag_id || item.hotspot_id}
                          className="p-4 border border-border rounded-lg hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="flex items-center gap-1">
                                {getTypeIcon(item.type)}
                                {getTypeLabel(item.type)}
                              </Badge>
                              {item.platform && (
                                <Badge variant="outline">
                                  {platformOptions.find(p => p.id === item.platform)?.name || item.platform}
                                </Badge>
                              )}
                              {item.industry && (
                                <span className="text-sm text-muted-foreground">
                                  {item.industry}
                                </span>
                              )}
                              {item.topic && (
                                <span className="text-sm text-muted-foreground">
                                  "{item.topic}"
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(item.created_at), "PPp", { locale: es })}
                            </span>
                          </div>
                          <p className="text-sm line-clamp-2 text-muted-foreground">
                            {item.analysis?.substring(0, 200)}...
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2"
                            onClick={() => {
                              if (item.type === "trends") {
                                setTrendsResult({ analysis: item.analysis, industry: item.industry });
                                setActiveTab("trends");
                              } else if (item.type === "hashtags") {
                                setHashtagsResult({ hashtags: item.analysis, topic: item.topic, platform: item.platform });
                                setActiveTab("hashtags");
                              } else if (item.type === "hotspots") {
                                setHotspotsResult({ hotspots: item.analysis, platform: item.platform, audience_type: item.audience_type });
                                setActiveTab("hotspots");
                              }
                            }}
                          >
                            Ver completo
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default TrendsPage;
